'use client'

import { getIdToken } from 'firebase/auth'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  BsChatLeftDots,
  BsFillChatLeftDotsFill,
  BsFillDice3Fill,
  BsFillGrid1X2Fill,
  BsLightningChargeFill,
  BsPencilFill,
  BsPersonBoundingBox,
  BsStack,
} from 'react-icons/bs'
import {
  FaCalendar,
  FaCameraRetro,
  FaChevronCircleDown,
  FaExpandAlt,
  FaFemale,
  FaFolder,
  FaGem,
  FaGlobeAmericas,
  FaLightbulb,
  FaClipboardList,
  FaLock,
  FaMagic,
  FaPalette,
  FaPlus,
  FaTheaterMasks,
  FaTrash,
  FaUnlock,
  FaWeight,
  FaEyeSlash,
  FaCarrot,
  FaChevronDown,
  FaSave,
} from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import { GiPeach, GiSnail } from 'react-icons/gi'
import { HiAdjustments } from 'react-icons/hi'
import { MdFaceRetouchingNatural } from 'react-icons/md'
import ClipLoader from 'react-spinners/ClipLoader'

import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { isSafari } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { IoBody, IoCloud, IoCloudOffline } from 'react-icons/io5'
import { useAuth } from '../contexts/AuthContext'
import { useUpgradeGate } from '../contexts/UpgradeGateContext'
import { CreateContext } from '../contexts/CreateContext'
import { useSessionFeed } from '../contexts/SessionFeedProvider'
import { clearCache } from '../helpers/mediaCache'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import {
  getDetails,
  mapValueToPercentage,
  randomizeSeed,
  isSafeCountry,
  flameImageGems,
  flameHighDetailGems,
} from '../helpers/helpers'
import BookmarkModalContent from './BookmarkModalContent'
import CharacterModal from './CharacterModal'
import EmotionModal from './EmotionModal'
import FiltersModal from './FiltersModal'
import LoraModal from './LoraModal'
import Modal from './Modal'
import ModelModal from './ModelModal'
import TutorialBanner from './TutorialBanner'

import { PiCherriesFill } from 'react-icons/pi'
import { sampleImagesCreate } from '../config/staticImages'

// Models with a realistic style (photoreal / hyperreal / legacy). AI Character
// v3 — the strongest apply — is only offered for these; anime, illustration and
// render styles fall back to v1/v2. This mirrors the backend's face-swap
// ("facet") model gate, which only runs on realistic styles. Keep in sync with
// the face_swap conditions in the backend (generate.py / generate_image.py).
// Hardcore Flame renders like Cinematic Flame (the backend's `family`), so it hides the same
// Anime-only sliders.
const CINEMATIC_FAMILY = ['Cinematic Flame', 'Hardcore Flame']

const REALISTIC_CHAR_MODELS = [
  'Real',
  'K-Pop',
  'Cinematic XL',
  'Cinematic Flame',
  'Hardcore Flame',
  'Hardcore XL',
  'Pony XL',
  'Pony XL 2',
  'Pony Photo XL',
  'Pony Photo XL 2',
]

const Inputs = ({
  // Variant B (simplified UI) reuses this full settings panel inside its drawer.
  // hidePromptAndActions: hide the prompt textarea, header (create/reset), and the
  //   sticky generate bar (those live in B's bottom PromptBar instead).
  // hideStyleButton: hide the Style/model picker (B surfaces it next to the ellipsis).
  hidePromptAndActions = false,
  // Optional node rendered at the start of the header row (Variant B passes the
  // Image/Animate mode toggle here so it sits on one line with bookmark/reset).
  headerLeft = null,
  hideStyleButton = false,
  // Variant B desktop: show the Style/model picker inside this panel; on mobile
  // it's hidden here (the top bar carries it). Renders the Style cell lg-only.
  styleButtonResponsive = false,
  model,
  setModel,
  imageSize,
  setImageSize,
  quality,
  setQuality,
  creativity,
  setCreativity,
  prompt,
  setPrompt,
  negativePrompt,
  setNegativePrompt,
  seed,
  setSeed,
  handleSubmit,
  subscription,
  isLoading,
  lora,
  setLora,
  lorasList,
  setLorasList,
  locked,
  setLocked,
  ntier,
  setNtier,
  faceFix,
  setFaceFix,
  chillMode,
  setChillMode,
  cooldown,
  cooldownProgress,
  setImageSrc,
  setImageHistory,
  filter,
  setFilter,
  details,
  setDetails,
  customPoseImage,
  setCustomPoseImage,
  setIsLimitModalOpen,
  setErrorText,
  charImage,
  setCharImage,
  recentCharacters = [],
  setIsErrorModalOpen,
  stickMode,
  setStickMode,
  emotion,
  setEmotion,
  characterVersion,
  setCharacterVersion,
  banned,
  folderSetting,
  setFolderSetting,
  folders,
  setFolders,
  autoSave,
  setAutoSave,
  setVisibilityMode,
  age,
  setAge,
  showAttributes,
  setShowAttributes,
  weight,
  setWeight,
  breasts,
  setBreasts,
  ass,
  setAss,
  penis,
  setPenis,
  nsfwEnabled,
  setNsfwEnabled,
  visibilityMode,
  setHasUnsavedChanges,
  bookmarkModalOpen,
  setBookmarkModalOpen,
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const safeCountry = isSafeCountry()

  // AI Character version options. v1.0 is SD-only (dropped for XL/Pony/Flame
  // tiers). v3.0 (strongest, full character apply) is only offered for realistic
  // styles — matching the backend face-swap ("facet") gate. v2.0 is available
  // everywhere.
  const isXlCharTier = ['XL', 'Pony', 'Flame'].some((item) =>
    model.includes(item),
  )
  const isRealisticCharStyle = REALISTIC_CHAR_MODELS.includes(model)
  const allowedCharVersions = [
    ...(isXlCharTier ? [] : ['v1']),
    'v2',
    ...(isRealisticCharStyle ? ['v3'] : []),
  ]

  // Copy shown per version in the character-version dropdown. Cost mirrors the
  // gem badge below (v3 = +3, the Qwen apply step; otherwise +1).
  const charVersionMeta = {
    v1: {
      tag: 'Legacy',
      desc: 'Legacy, with basic likeness.',
      cost: '+1',
    },
    v2: {
      tag: 'Standard',
      desc: 'Fast, with improved proportions over v1.',
      cost: '+1',
    },
    v3: {
      tag: 'Best',
      desc: 'Highest quality with the most flexibility.',
      cost: '+3',
    },
  }

  // Strongest version compatible with the current model. allowedCharVersions is
  // ordered weakest→strongest (v1, v2, v3), so the last entry is the strongest.
  const highestCharVersion = allowedCharVersions[allowedCharVersions.length - 1]

  // AI Character v3 recreates the applied character's face AND body, so it
  // derives age/weight/body attributes from the character itself. The manual
  // Attributes sliders are redundant there and would fight the applied
  // character, so we force them off whenever v3 is actively applied (a version
  // pick only takes effect once a character image is set). Re-enabling them is
  // blocked behind an explainer modal that offers a drop to v2 (which does
  // support manual attributes).
  const attributesLockedByCharacter = !!charImage && characterVersion === 'v3'

  // Whether the user has manually picked a version from the dropdown. A manual
  // pick is "pinned" and persists across model changes as long as it stays
  // compatible; until then — and after an incompatible model forces a change —
  // we follow the strongest compatible version by default.
  const userPickedCharVersionRef = useRef(false)

  // Keep the AI Character version sensible when the model changes:
  //   • manual pick still valid  → keep it (persists, e.g. stays v2)
  //   • manual pick now invalid  → snap to the strongest compatible version and
  //                                drop the pin (fall back to the default)
  //   • no manual pick           → default to the strongest compatible version
  //                                (e.g. v3 on realistic models)
  // Fires on model switch only; the pin is set in the dropdown handler below.
  useEffect(() => {
    if (!allowedCharVersions.includes(characterVersion)) {
      userPickedCharVersionRef.current = false
      setCharacterVersion(highestCharVersion)
    } else if (!userPickedCharVersionRef.current) {
      setCharacterVersion(highestCharVersion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  const [charVersionMenuOpen, setCharVersionMenuOpen] = useState(false)
  const charVersionMenuRef = useRef(null)

  // Close the character-version dropdown when clicking outside it.
  useEffect(() => {
    if (!charVersionMenuOpen) return
    const handleClickOutside = (event) => {
      if (
        charVersionMenuRef.current &&
        !charVersionMenuRef.current.contains(event.target)
      ) {
        setCharVersionMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [charVersionMenuOpen])
  const [isLorasModalOpen, setIsLorasModalOpen] = useState(false)
  const [isModelsModalOpen, setIsModelsModalOpen] = useState(false)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [isEmotionModalOpen, setIsEmotionModalOpen] = useState(false)
  const [xlModalOpen, setXlModalOpen] = useState(false)
  const [ponyModalOpen, setPonyModalOpen] = useState(false)

  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [characterModalOpen, setCharacterModal] = useState(false)
  const [
    attributesFromCharacterModalOpen,
    setAttributesFromCharacterModalOpen,
  ] = useState(false)
  const [safeWarningOpen, setSafeWarningOpen] = useState(false)
  const [warning, setWarning] = useState('')
  const [negativeWarning, setNegativeWarning] = useState('')
  const [moreSettingsOpen, setMoreSettingsOpen] = useState(
    // safeGetItem is SSR-safe (returns null) and never throws — a bare localStorage
    // access here crashes the render on iOS Safari with storage blocked.
    () => safeGetItem('createMoreSettingsOpen') === 'true',
  )

  useEffect(() => {
    safeSetItem('createMoreSettingsOpen', moreSettingsOpen ? 'true' : 'false')
  }, [moreSettingsOpen])

  useEffect(() => {
    if (attributesLockedByCharacter && showAttributes) {
      setShowAttributes(false)
    }
  }, [attributesLockedByCharacter, showAttributes, setShowAttributes])

  const handleAttributesToggle = () => {
    if (attributesLockedByCharacter) {
      setAttributesFromCharacterModalOpen(true)
      return
    }

    setShowAttributes((prev) => !prev)
  }

  const removeCharacterAndEnableAttributes = () => {
    setCharImage(null)
    setShowAttributes(true)
  }

  const {
    user,
    safe,
    newUser,
    isPlusOrHigher,
    isPremiumOrHigher,
    isStudioOrHigher,
  } = useAuth()
  const { openUpgrade, requireFeature } = useUpgradeGate()
  const { setBFeed, bPendingIdRef, bPendingSourceRef } = useSessionFeed()
  const { resetCreateSession } = useContext(CreateContext)

  // Reset the create session. `scope` = 'all' wipes prompts, settings, attributes
  // and media; 'media' clears only the generated images/videos. The on-screen
  // feed, unsaved flag and media cache are cleared for both scopes.
  const handleReset = (scope) => {
    resetCreateSession({
      scope,
      model: 'Pony Anime XL',
      quality: isPlusOrHigher ? 'Extreme' : 'Ultra',
    })
    setHasUnsavedChanges(false)
    setBFeed([])
    bPendingIdRef.current = null
    bPendingSourceRef.current = null
    clearCache(user?.uid)
    setClearModalOpen(false)
  }

  // Flame renders at Extreme, except Max on a model with a paid High detail render
  // (helpers flameHighDetailGems — Hardcore Flame's full settings).
  useEffect(() => {
    if (
      model.includes('Flame') &&
      quality !== 'Extreme' &&
      !(quality === 'Max' && flameHighDetailGems(model) > 0)
    ) {
      setQuality('Extreme')
    }
  }, [model, quality])

  // Switching INTO a Flame model starts it at Extreme: a Max carried over from another model
  // would otherwise silently buy its High detail render. (Not on mount, so a Max chosen on
  // the model itself survives a reload.)
  const prevModelRef = useRef(model)
  useEffect(() => {
    if (prevModelRef.current !== model && model.includes('Flame')) {
      setQuality('Extreme')
    }
    prevModelRef.current = model
  }, [model])

  // Casual is Standard quality only and never on the XL 2 (Flame) models (backend
  // myapp/casual.py). This used to switch it off on every XL model, which undid 952bb64's
  // "casual on XL" (2026-10-01) on the first model change or page load.
  useEffect(() => {
    if (quality !== 'Ultra' || model.includes('Flame')) {
      setChillMode(false)
    }
  }, [quality, model])

  // A casual render is always one image (casual.py limit() sets batch_size 1), so the grid
  // shows 1 rather than a remembered 4 that the backend would quietly make 1.
  useEffect(() => {
    if (chillMode && ntier !== 1) setNtier(1)
  }, [chillMode, ntier])

  useEffect(() => {
    const fetchFolders = async () => {
      if (!user || !moreSettingsOpen || visibilityMode === 'Offline') return

      const idToken = await getIdToken(user)

      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_unique_folders_private`,
          { method: 'GET', headers: { Authorization: idToken } },
        )

        if (!response.ok) throw new Error('Failed to fetch folders')

        const data = await response.json()
        if (data.folders && data.folders.length > 0) {
          setFolders([
            'Gallery',
            ...data.folders
              .filter((f) => f && f !== 'Gallery')
              .sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' }),
              ),
          ])
        }
      } catch (error) {
        console.error('Error fetching folders:', error)
      }
    }

    fetchFolders()
  }, [user, moreSettingsOpen, visibilityMode])

  useEffect(() => {
    if (filter.includes('XL') && !model.includes('XL')) {
      setFilter('Default')
    }
  }, [model, filter])

  useEffect(() => {
    if (!isPlusOrHigher && visibilityMode !== 'Offline') {
      setVisibilityMode('Offline')
    }
    if (charImage && !charImage.includes('http')) {
      setVisibilityMode('Offline')
    }
  }, [visibilityMode, charImage, isPlusOrHigher])

  const toggleFaceFix = () => {
    if (!isPlusOrHigher) {
      openUpgrade('faceEnhance')
      setFaceFix(false)
      return
    }
    if (
      model === 'Anime' ||
      model === 'Hyperanime' ||
      model === 'Fur' ||
      model === 'Furtoon' ||
      model === 'Anime XL' ||
      model === 'Pony Anime XL' ||
      model === 'Pony Render XL'
    ) {
      setErrorText(
        'Face Enhance is only available for realistic Styles at this time.',
      )
      setIsErrorModalOpen(true)
      setFaceFix(false)
      return
    }
    if (charImage) {
      setErrorText(
        "No need to enable Face Enhance, it's enabled by default with AI Character!",
      )
      setIsErrorModalOpen(true)
      setFaceFix(false)
      return
    }
    setFaceFix(!faceFix)
  }

  useEffect(() => {
    if (
      model === 'Anime' ||
      model === 'Hyperanime' ||
      model === 'Fur' ||
      model === 'Furtoon' ||
      model === 'Anime XL' ||
      model === 'Pony Anime XL'
    ) {
      setFaceFix(false)
    }
  }, [setFaceFix, model])

  useEffect(() => {
    const allowed = ['512x512', '512x768', '768x512']
    if (typeof imageSize !== 'string' || !allowed.includes(imageSize.trim())) {
      setImageSize('512x768')
    }
  }, [imageSize, setImageSize])

  const qualityInitializedRef = useRef(false)
  useEffect(() => {
    if (!subscription?.plan) return
    if (!isStudioOrHigher && quality === 'Max') {
      setQuality(isPlusOrHigher ? 'Extreme' : 'Ultra')
      qualityInitializedRef.current = true
      return
    }
    // Flame models require Extreme (forced by the effect above), so never
    // downgrade them here — doing so ping-pongs with that effect into an
    // infinite render loop for non-Plus users on a Flame model.
    if (!isPlusOrHigher && quality === 'Extreme' && !model.includes('Flame')) {
      setQuality('Ultra')
      qualityInitializedRef.current = true
      return
    }
    if (
      !qualityInitializedRef.current &&
      isPlusOrHigher &&
      quality === 'Ultra'
    ) {
      setQuality('Extreme')
    }
    qualityInitializedRef.current = true
  }, [
    subscription?.plan,
    isPlusOrHigher,
    isStudioOrHigher,
    quality,
    setQuality,
    model,
  ])

  const setQualityAndNtier = (quality) => {
    setQuality(quality)
    if (quality !== 'Ultra') {
      setNtier(1)
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (prompt.length > 250) {
        setWarning(
          'Your prompt is getting long. This may cause unwanted artefacts!',
        )
      } else if (prompt.split('(').length - 1 > 6) {
        setWarning(
          'Your prompt contains too many parentheses. This may cause unwanted artefacts!',
        )
      } else {
        setWarning('')
      }

      if (negativePrompt.length > 250) {
        setNegativeWarning(
          'Your negative prompt is getting long. This may cause unwanted artefacts!',
        )
      } else if (negativePrompt.split('(').length - 1 > 6) {
        setNegativeWarning(
          'Your negative prompt contains too many parentheses. This may cause unwanted artefacts!',
        )
      } else {
        setNegativeWarning('')
      }
    }, 2000)

    return () => {
      clearTimeout(handler)
    }
  }, [prompt, negativePrompt])

  return (
    <>
      <CharacterModal
        isOpen={characterModalOpen}
        closeModal={() => setCharacterModal(false)}
        recentCharacters={recentCharacters}
        charImage={charImage}
        setCharImage={setCharImage}
        onViewExplore={() => {
          setCharacterModal(false)
          router.push('/explore')
        }}
        onViewProfile={() => {
          setCharacterModal(false)
          router.push(`/profile/${user?.uid}`)
        }}
      />

      <Modal
        isOpen={xlModalOpen}
        closeModal={() => setXlModalOpen(false)}
        title={t('xlFilter')}
        content={t('xlFilterDescription')}
        confirmation={t('done')}
        onConfirm={() => {
          setXlModalOpen(false)
        }}
      />

      <Modal
        isOpen={ponyModalOpen}
        closeModal={() => setPonyModalOpen(false)}
        title={t('xlPlusFilter')}
        content={t('xlPlusFilterDescription')}
        confirmation={t('done')}
        onConfirm={() => {
          setPonyModalOpen(false)
        }}
      />

      <Modal
        isOpen={attributesFromCharacterModalOpen}
        closeModal={() => setAttributesFromCharacterModalOpen(false)}
        title={t('attributesFromCharacterTitle')}
        content={
          <div>
            <p className="text-white text-base leading-relaxed">
              {t('attributesFromCharacterV3Body')}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">
              {t('attributesFromCharacterV2Note')}
            </p>
          </div>
        }
        confirmation={t('keepCharacter')}
        confirmation2={t('removeCharacter')}
        onConfirm2={removeCharacterAndEnableAttributes}
      />

      <EmotionModal
        isOpen={isEmotionModalOpen}
        closeModal={() => setIsEmotionModalOpen(false)}
        subscription={subscription?.plan}
        emotion={emotion}
        setEmotion={setEmotion}
        model={model}
        safe={safeCountry}
      />

      <LoraModal
        isOpen={isLorasModalOpen}
        closeModal={() => setIsLorasModalOpen(false)}
        subscription={subscription}
        lora={lora}
        setLora={setLora}
        lorasList={lorasList}
        setLorasList={setLorasList}
        model={model}
        customPoseImage={customPoseImage}
        setCustomPoseImage={setCustomPoseImage}
        setIsLimitModalOpen={setIsLimitModalOpen}
        setErrorText={setErrorText}
        charImage={charImage}
        setIsErrorModalOpen={setIsErrorModalOpen}
        user={user}
        setShowAttributes={setShowAttributes}
      />

      <ModelModal
        isOpen={isModelsModalOpen}
        closeModal={() => setIsModelsModalOpen(false)}
        subscription={subscription?.plan}
        model={model}
        setModel={setModel}
        setLora={setLora}
        setFilter={setFilter}
        user={user}
        newUser={newUser}
        safe={safeCountry}
      />

      <FiltersModal
        isOpen={isFiltersModalOpen}
        closeModal={() => setIsFiltersModalOpen(false)}
        subscription={subscription?.plan}
        filter={filter}
        setFilter={setFilter}
        model={model}
        setXlModalOpen={setXlModalOpen}
        setPonyModalOpen={setPonyModalOpen}
        safe={safeCountry}
      />

      <Modal
        isOpen={clearModalOpen}
        closeModal={() => setClearModalOpen(false)}
        title={t('resetQuestion')}
        content={
          <div className="space-y-3 text-base">
            <p>{t('chooseResetAmount')}</p>
            <ul className="space-y-2">
              <li>
                <span className="font-semibold">{t('clearContent')}</span> -{' '}
                {t('clearContentDescription')}
              </li>
              <li>
                <span className="font-semibold">{t('resetEverything')}</span> -{' '}
                {t('resetEverythingDescription')}
              </li>
            </ul>
          </div>
        }
        confirmation={t('resetEverything')}
        confirmation2={t('clearContent')}
        onConfirm={() => handleReset('all')}
        onConfirm2={() => handleReset('media')}
      />

      <Modal
        isOpen={bookmarkModalOpen}
        closeModal={() => setBookmarkModalOpen(false)}
        title={t('savedWorkflows')}
        content={
          <BookmarkModalContent
            user={user}
            model={model}
            setModel={setModel}
            prompt={prompt}
            setPrompt={setPrompt}
            negativePrompt={negativePrompt}
            setNegativePrompt={setNegativePrompt}
            lora={lora}
            setLora={setLora}
            setLorasList={setLorasList}
            creativity={creativity}
            setCreativity={setCreativity}
            imageSize={imageSize}
            setImageSize={setImageSize}
            seed={seed}
            setSeed={setSeed}
            setQuality={setQuality}
            setDetails={setDetails}
            setAge={setAge}
            setWeight={setWeight}
            setBreasts={setBreasts}
            setAss={setAss}
            setFilter={setFilter}
            setFaceFix={setFaceFix}
            cloneLoading={isLoading}
            setModalVisible={setBookmarkModalOpen}
            modalVisible={bookmarkModalOpen}
            video={false}
          />
        }
        confirmation={t('done')}
        onConfirm={() => setBookmarkModalOpen(false)}
      />
      <Modal
        isOpen={safeWarningOpen}
        closeModal={() => {
          setSafeWarningOpen(false)
        }}
        title={t('safeModeEnabledTitle')}
        content={
          <div>
            <p className="text-white">
              {t('safeModeFeatureBlockedDescription')}
            </p>
          </div>
        }
        confirmation={t('settings')}
        onConfirm={() => router.push('/settings')}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        className="w-full bg-[var(--middle-bg-color)] rounded-xl p-2"
      >
        <div className="p-2 md:min-h-screen">
          <div className={headerLeft ? 'mb-3 @container' : 'mb-3'}>
            <div
              className={`flex items-center gap-2 ${
                hidePromptAndActions && !headerLeft ? 'justify-end' : ''
              }`}
            >
              {/* Variant B (hidePromptAndActions) drops the "Create" heading and
                  right-aligns the workflow/reset buttons; when a headerLeft node
                  is supplied (the Image/Animate toggle) it fills the left of the
                  row instead. Variant A is unchanged. */}
              {headerLeft ? (
                <div className="flex-1 min-w-0">{headerLeft}</div>
              ) : (
                !hidePromptAndActions && (
                  <div className="text-white font-bold text-base tracking-tight mr-auto">
                    {t('create')}
                  </div>
                )
              )}

              <button
                type="button"
                title={t('savedWorkflows')}
                aria-label={t('savedWorkflows')}
                onClick={() => {
                  setBookmarkModalOpen(true)
                }}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <FaClipboardList />
              </button>
              <button
                type="button"
                onClick={() => {
                  setClearModalOpen(true)
                }}
                className="h-9 px-3 flex items-center justify-center gap-2 rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white text-xs font-semibold [@container(max-width:20rem)]:[&>span]:sr-only transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <span>{t('reset')}</span>
                <FiRefreshCw />
              </button>
            </div>
          </div>
          <hr className="my-4 border-[var(--foreground-bg-color)]" />

          <TutorialBanner
            storageKey="imageTutorialSeen"
            href="/articles/promptchan-tutorial"
            label={t('howToCreate')}
          />

          {safe && !safeCountry && (
            <Link
              href="/settings"
              className="my-2 flex w-full items-center gap-2 rounded-2xl border border-[#7a74eb]/25 bg-[#6366f1]/10 px-3 py-2 text-xs hover:bg-[#6366f1]/20 transition"
            >
              <FaEyeSlash className="text-[#a5b4fc] shrink-0" size={12} />
              <span className="text-gray-300 flex-1">
                <span className="font-medium text-[#c7d2fe]">
                  {t('safeMode')}
                </span>
                : {t('nsfwHidden')}
              </span>
              <span className="shrink-0 text-[10px] font-semibold text-[#c7d2fe]">
                {t('update')} ›
              </span>
            </Link>
          )}

          <>
            {!hidePromptAndActions && (
              <div className="mb-5">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <BsFillChatLeftDotsFill className="text-white" size={12} />
                  {t('prompt')}
                </label>
                <textarea
                  type="text"
                  id="prompt"
                  placeholder={t('enterYourPrompt')}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full h-36 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                  required
                />
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('enterAPrompt')}
                </p>
                {warning && (
                  <p className="mt-2 inline-block rounded-md bg-orange-400/10 px-2 py-1 text-orange-300 text-xs font-semibold">
                    {warning}
                  </p>
                )}
              </div>
            )}

            {/* Variant B (hidePromptAndActions) moves the positive prompt to the
                bottom PromptBar, which left this negative box orphaned at the top
                looking like "the prompt". Hide it here for B and render it inside
                "More settings" below instead. Variant A is unchanged. */}
            {!hidePromptAndActions && (
              <div className="mb-5">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <BsChatLeftDots className="text-white" size={12} />
                  {t('negativePrompt')}
                </label>
                <textarea
                  type="text"
                  id="negativePrompt"
                  placeholder={t('enterYourNegativePrompt')}
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  className="w-full h-16 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                />
                {negativeWarning && (
                  <p className="mt-2 inline-block rounded-md bg-orange-400/10 px-2 py-1 text-orange-300 text-xs font-semibold">
                    {negativeWarning}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('enterANegativePrompt')}
                </p>
              </div>
            )}

            <div
              className={`grid gap-3 ${
                hideStyleButton
                  ? 'grid-cols-1'
                  : styleButtonResponsive
                    ? 'grid-cols-1 lg:grid-cols-2'
                    : 'grid-cols-2'
              }`}
            >
              {!hideStyleButton && (
                <div
                  className={`mb-1 ${
                    // Hidden only where the phone top bar carries the model button
                    // (below md). From md the settings panel is static and that bar
                    // is gone, so this is the sole way to reach the picker. Column
                    // count stays on lg: 320px of panel at md fits one.
                    styleButtonResponsive ? 'hidden md:block' : ''
                  }`}
                >
                  <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <FaPalette className="text-white" size={12} />
                    {t('style')}
                    <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {t('new')}
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={() => setIsModelsModalOpen(true)}
                    className="group relative w-full rounded-2xl px-4 py-3 pr-9 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start transition-colors"
                  >
                    <span className="truncate block">
                      {t(model?.toLowerCase())}
                    </span>
                    <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 opacity-70 group-hover:opacity-100 transition" />
                  </button>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('chooseTheAiModel')}
                  </p>
                </div>
              )}

              {!safeCountry && (
                <div className="mb-1">
                  <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <FaFemale className="text-white" size={12} />
                    {t('poses')}
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      if (safe !== true) {
                        setIsLorasModalOpen(true)
                      } else {
                        setSafeWarningOpen(true)
                      }
                    }}
                    className={`group relative w-full rounded-2xl px-4 py-3 pr-9 text-white border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start overflow-hidden transition-colors ${
                      lora !== 'Custom' ? 'bg-[var(--middle-bg-color)]' : ''
                    }`}
                    style={
                      lora === 'Custom'
                        ? {
                            backgroundImage: `url(${customPoseImage})`,
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                          }
                        : {}
                    }
                  >
                    {lora === 'Custom' && (
                      <span className="absolute inset-0 bg-black/35 pointer-events-none" />
                    )}
                    <span
                      style={{
                        textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
                      }}
                      className="relative truncate block"
                    >
                      {lorasList?.length > 0 ? 'Multiple' : t(lora)}
                    </span>
                    <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-200 opacity-70 group-hover:opacity-100 transition" />
                  </button>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('chooseThePose')}
                  </p>
                </div>
              )}

              {/* Custom Pose interpretation: Depth (openpose off) keeps fine detail;
                  Position (stickMode) uses just the pose. Studio+ feature; sent to the
                  backend as `stickMode`. */}
              {lora === 'Custom' && !model.includes('XL') && (
                <div className="mb-1">
                  <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <HiAdjustments className="text-white" size={12} />
                    Custom Pose
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        isStudioOrHigher
                          ? setStickMode(false)
                          : router.push('/upgrade')
                      }
                      className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                        !stickMode
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      Depth
                      {!isStudioOrHigher && <FaLock size={8} />}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        isStudioOrHigher
                          ? setStickMode(true)
                          : router.push('/upgrade')
                      }
                      className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                        stickMode
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      Position
                      {!isStudioOrHigher && <FaLock size={8} />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    Depth replicates fine details. Position uses the pose only.
                    {!isStudioOrHigher && (
                      <>
                        {' '}
                        <Link
                          href={'/upgrade'}
                          className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                        >
                          {t('upgrade')}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
              )}

              <div className="mb-1">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <FaCameraRetro className="text-white" size={12} />
                  {t('filters')}
                </label>

                <button
                  type="button"
                  onClick={() => setIsFiltersModalOpen(true)}
                  className="group relative w-full rounded-2xl px-4 py-3 pr-9 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start transition-colors"
                >
                  <span className="truncate block">
                    {t(filter?.toLowerCase())}
                  </span>
                  <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 opacity-70 group-hover:opacity-100 transition" />
                </button>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('chooseTheFilter')}
                </p>
              </div>

              <div className="mb-1">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <FaTheaterMasks className="text-white" size={12} />
                  {t('emotion')}
                </label>

                <button
                  type="button"
                  onClick={() => {
                    setIsEmotionModalOpen(true)
                  }}
                  className="group relative w-full rounded-2xl px-4 py-3 pr-9 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start transition-colors"
                >
                  <span className="truncate block">{emotion}</span>
                  <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 opacity-70 group-hover:opacity-100 transition" />
                </button>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('chooseTheEmotion')}
                </p>
              </div>
            </div>

            <hr className="my-5 border-[var(--foreground-bg-color)]" />

            {/* Variant B: negative prompt lives in the main settings (its top slot is
                hidden so it doesn't masquerade as the main prompt, which moved to the
                bottom PromptBar). Placed just above the AI Character section. */}
            {hidePromptAndActions && (
              <div className="mb-5">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <BsChatLeftDots className="text-white" size={12} />
                  {t('negativePrompt')}
                </label>
                <textarea
                  type="text"
                  id="negativePrompt"
                  placeholder={t('enterYourNegativePrompt')}
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  className="w-full h-16 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                />
                {negativeWarning && (
                  <p className="mt-2 inline-block rounded-md bg-orange-400/10 px-2 py-1 text-orange-300 text-xs font-semibold">
                    {negativeWarning}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('enterANegativePrompt')}
                </p>
              </div>
            )}

            {user && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-white text-sm font-semibold flex items-center gap-2">
                    <BsPersonBoundingBox className="text-white" size={12} />
                    {t('character')}
                  </label>

                  {/* AI Character apply version. XL/Pony/Flame → {v2.0, v3.0};
                      SD → {v1.0, v2.0, v3.0}. v3.0 = strongest, full character
                      apply. Dropdown replaces the old click-to-cycle pill. */}
                  <div className="relative" ref={charVersionMenuRef}>
                    <button
                      type="button"
                      onClick={() => setCharVersionMenuOpen((open) => !open)}
                      aria-haspopup="listbox"
                      aria-expanded={charVersionMenuOpen}
                      className={`${
                        characterVersion === 'v3'
                          ? 'bg-[var(--main-bg-color)]'
                          : 'bg-[var(--secondary-bg-color)]'
                      } text-white text-[10px] font-bold px-2 py-0.5 rounded-full button-glow flex items-center justify-center gap-1 cursor-pointer`}
                    >
                      {`${characterVersion}.0`}
                      <FaChevronCircleDown
                        className={`transition-transform ${
                          charVersionMenuOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {charVersionMenuOpen && (
                      <div
                        role="listbox"
                        className="absolute left-0 top-full mt-1.5 z-30 w-60 rounded-2xl border border-[var(--foreground-bg-color)] bg-[var(--middle-bg-color)] p-1.5 shadow-xl"
                      >
                        {/* Show newest→oldest (v3 at top, v1 at bottom).
                            allowedCharVersions stays weakest→strongest for the
                            "highest compatible" logic, so reverse a copy here. */}
                        {[...allowedCharVersions].reverse().map((version) => {
                          const meta = charVersionMeta[version]
                          const active = characterVersion === version
                          return (
                            <button
                              type="button"
                              key={version}
                              role="option"
                              aria-selected={active}
                              onClick={() => {
                                userPickedCharVersionRef.current = true
                                setCharacterVersion(version)
                                setCharVersionMenuOpen(false)
                              }}
                              className={`w-full text-left rounded-xl px-2.5 py-2 transition-colors ${
                                active
                                  ? 'bg-[var(--main-bg-color)]'
                                  : 'hover:bg-[var(--foreground-bg-color)]'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-white text-xs font-bold">
                                  {`${version}.0`}
                                </span>
                                <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-300">
                                  {meta.tag}
                                </span>
                                <span className="ml-auto flex items-center text-white text-[10px] font-semibold">
                                  <FaGem className="mr-1" size={8} />
                                  {meta.cost}
                                </span>
                              </div>
                              <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                                {meta.desc}
                              </p>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {charImage && (
                    <span className="flex items-center text-white text-[11px] ml-auto bg-[var(--foreground-bg-color)] rounded-full px-2.5 py-0.5">
                      <FaGem className="mr-1" size={10} />
                      <span className="font-semibold">
                        {characterVersion === 'v3' ? '+3' : '+1'}
                      </span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div
                    title={t('pickACharacter')}
                    onClick={() => {
                      // Gated on Pro, not Plus: generate_bp.py rejects a non-preset reference
                      // for anything below it, so opening the picker for Plus/Premium only
                      // walked them into a 403 after they'd picked a face.
                      if (!requireFeature('aiCharacter')) return
                      setCharacterModal(true)
                    }}
                    className="relative w-11 h-11 flex items-center justify-center rounded-full border border-[var(--foreground-bg-color)] hover:border-gray-400 bg-[var(--middle-bg-color)] cursor-pointer transition-colors"
                  >
                    {charImage ? (
                      <>
                        <img
                          src={charImage}
                          alt="Character"
                          className="absolute inset-0 w-full h-full object-cover rounded-full"
                        />
                        <button
                          type="button"
                          title={t('removeCharacter')}
                          aria-label={t('removeCharacter')}
                          className="absolute -top-1 -right-1 z-10 w-5 h-5 flex items-center justify-center text-[10px] text-white bg-red-500/90 hover:bg-red-500 rounded-full shadow"
                          onClick={(e) => {
                            e.stopPropagation()
                            setCharImage(null)
                          }}
                        >
                          <FaTrash size={8} />
                        </button>
                      </>
                    ) : (
                      <FaPlus className="text-white text-sm" />
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    {sampleImagesCreate.map((image) => (
                      <div
                        key={image.id}
                        onClick={() => {
                          setCharImage(image.imageUrl)
                        }}
                        className="w-11 h-11 rounded-full border border-[var(--foreground-bg-color)] hover:border-gray-400 bg-[var(--middle-bg-color)] cursor-pointer overflow-hidden transition-colors"
                      >
                        <img
                          src={image.imageUrl}
                          alt={image.prompt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('selectAnAi')}
                  {!isStudioOrHigher && (
                    <>
                      {' '}
                      <Link
                        href={'/upgrade'}
                        className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                      >
                        {t('upgrade')}
                      </Link>
                    </>
                  )}
                </p>
              </div>
            )}

            <hr className="my-5 border-[var(--foreground-bg-color)]" />

            {!safeCountry && (
              <div className="mb-5">
                <div className="flex items-center mb-2">
                  <span className="text-white text-sm font-semibold flex items-center gap-2">
                    <IoBody className="text-white" size={12} />
                    {t('attributes')}
                  </span>
                  <div
                    className={`ml-auto relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                      showAttributes && !attributesLockedByCharacter
                        ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                        : 'bg-[var(--foreground-bg-color)]'
                    }`}
                    onClick={handleAttributesToggle}
                    role="switch"
                    aria-checked={
                      showAttributes && !attributesLockedByCharacter
                    }
                    aria-disabled={attributesLockedByCharacter}
                    title={
                      attributesLockedByCharacter
                        ? t('attributesFromCharacterHint')
                        : undefined
                    }
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                        showAttributes && !attributesLockedByCharacter
                          ? 'translate-x-6 bg-white'
                          : 'translate-x-0 bg-gray-500'
                      }`}
                    />
                  </div>
                </div>

                <p className="text-gray-400 text-xs leading-relaxed mb-2">
                  {attributesLockedByCharacter
                    ? t('attributesFromCharacterHint')
                    : t('adjustYourCharacter')}
                </p>

                <div
                  className={`w-10 h-0.5 bg-[var(--secondary-bg-color)] rounded-full transition-[max-height,opacity,margin] duration-300 ease-in-out overflow-hidden mx-auto mt-2 ${
                    !showAttributes
                      ? 'max-h-40 opacity-100'
                      : 'max-h-0 opacity-0 mt-0'
                  }`}
                ></div>

                <div
                  className={`transition-[max-height,opacity,margin,padding] duration-300 ease-in-out px-1 overflow-hidden ${
                    showAttributes
                      ? 'opacity-100 mt-4 pt-2'
                      : 'max-h-0 opacity-0 mt-0 pt-0 pointer-events-none'
                  }`}
                >
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <FaCalendar className="text-white" size={12} />
                      {t('age')}
                    </span>
                    <input
                      type="range"
                      min={18}
                      max={80}
                      value={age}
                      onChange={(e) => setAge(parseInt(e.target.value, 10))}
                      className="slider flex-1"
                    />
                    <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                      {age}
                    </div>
                  </div>

                  {/* Hardcore Flame's image has no weight-slider LoRA, and weight has no
                      prompt text to fall back on (backend `lora_files: False`). */}
                  {model !== 'Hardcore Flame' && (
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaWeight className="text-white" size={12} />
                        {t('weight')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={weight}
                        onChange={(e) => setWeight(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(weight)}
                      </div>
                    </div>
                  )}

                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <PiCherriesFill className="text-white" size={12} />
                      {t('breastSize')}
                    </span>
                    <input
                      type="range"
                      min={-1}
                      max={1}
                      step={0.05}
                      value={breasts}
                      onChange={(e) => setBreasts(parseFloat(e.target.value))}
                      className="slider flex-1"
                    />
                    <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                      {mapValueToPercentage(breasts)}
                    </div>
                  </div>

                  {!CINEMATIC_FAMILY.includes(model) && (
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <GiPeach className="text-white" size={12} />
                        {t('assSize')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={ass}
                        onChange={(e) => setAss(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(ass)}
                      </div>
                    </div>
                  )}

                  {model?.includes('Anime Flame') && (
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaCarrot className="text-white" size={12} />
                        {t('penisSize')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={penis}
                        onChange={(e) => setPenis(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(penis)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <hr className="my-5 border-[var(--foreground-bg-color)]" />

            <button
              type="button"
              onClick={() => setMoreSettingsOpen((v) => !v)}
              aria-expanded={moreSettingsOpen}
              className="w-full flex items-center justify-between gap-2 px-4 h-11 mb-4 rounded-full bg-[var(--foreground-bg-color)]/70 hover:bg-[var(--secondary-bg-color)] text-white text-sm font-semibold transition-colors active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
            >
              <span className="flex items-center gap-2">
                <HiAdjustments className="text-base" />
                {t('moreSettings')}
              </span>
              <FaChevronDown
                className={`text-[10px] transition-transform duration-200 ${
                  moreSettingsOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {moreSettingsOpen && (
              <>
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <FaExpandAlt className="text-white" size={12} />
                      {t('aspect')}
                    </span>
                    <div className="flex flex-1 gap-1">
                      <button
                        type="button"
                        onClick={() => setImageSize('512x512')}
                        className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                          imageSize === '512x512'
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                        }`}
                      >
                        {t('square')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageSize('512x768')}
                        className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                          imageSize === '512x768'
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                        }`}
                      >
                        {t('portrait')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageSize('768x512')}
                        disabled={!isPremiumOrHigher}
                        className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                          imageSize === '768x512'
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                        } ${
                          !isPremiumOrHigher
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {t('wide')}
                        {!isPremiumOrHigher && <FaLock size={9} />}
                      </button>
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('selectTheAspectRatio')}
                    {!isPremiumOrHigher && (
                      <>
                        {' '}
                        <Link
                          href="/upgrade"
                          className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                        >
                          {t('upgrade')}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
                {/* Flame has no quality tiers, except a model with a paid High detail
                    render: Extreme (its price) or Max (+flameHighDetailGems). */}
                {(!model.includes('Flame') ||
                  flameHighDetailGems(model) > 0) && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <BsStack className="text-white" size={12} />
                        {t('quality')}
                        {model.includes('Flame') ? (
                          quality === 'Max' && (
                            <span className="flex items-center text-white">
                              <FaGem size={9} />
                              <span className="font-semibold text-[10px] ml-0.5">
                                +{flameHighDetailGems(model)}
                              </span>
                            </span>
                          )
                        ) : quality === 'Extreme' ? (
                          <span className="flex items-center text-white">
                            <FaGem size={9} />
                            <span className="font-semibold text-[10px] ml-0.5">
                              +1
                            </span>
                          </span>
                        ) : quality === 'Max' ? (
                          <span className="flex items-center text-white">
                            <FaGem size={9} />
                            <span className="font-semibold text-[10px] ml-0.5">
                              +2
                            </span>
                          </span>
                        ) : null}
                      </span>
                      <div className="flex flex-1 gap-1">
                        {!model.includes('Flame') && (
                          <button
                            type="button"
                            onClick={() => setQualityAndNtier('Ultra')}
                            className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                              quality === 'Ultra'
                                ? 'bg-white text-black'
                                : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                            }`}
                          >
                            {t('ultra')}
                          </button>
                        )}
                        {/* Locked tiers stay CLICKABLE (dimmed + padlock, as before) and open
                            the feature modal. `disabled` made them inert: the only explanation
                            of what Extreme/Max are, or which plan has them, was a padlock. */}
                        <button
                          type="button"
                          onClick={() => {
                            if (!requireFeature('qualityExtreme')) return
                            setQualityAndNtier('Extreme')
                          }}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            quality === 'Extreme'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${!isPlusOrHigher ? 'opacity-50' : ''}`}
                        >
                          {t('extreme')}
                          {!isPlusOrHigher && <FaLock size={9} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!requireFeature('qualityMax')) return
                            setQualityAndNtier('Max')
                          }}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors button-glow inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            quality === 'Max'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${!isStudioOrHigher ? 'opacity-50' : ''}`}
                        >
                          {t('max')}
                          {!isStudioOrHigher && <FaLock size={9} />}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectTheDesiredOutputImage')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                {!CINEMATIC_FAMILY.includes(model) && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <BsPencilFill className="text-white" size={12} />
                        {t('details')}
                      </span>
                      <input
                        type="range"
                        min={-2}
                        max={2}
                        value={details}
                        onChange={(e) =>
                          setDetails(parseInt(e.target.value, 10))
                        }
                        className="slider flex-1"
                      />
                      <div className="text-white w-14 font-semibold text-xs text-right shrink-0 tabular-nums">
                        {getDetails(details)}
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('adjustTheDetail')}
                    </p>
                  </div>
                )}

                {!model.includes('Flame') && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="seed"
                        className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24"
                      >
                        <BsFillGrid1X2Fill className="text-white" size={12} />
                        {t('imageGrid')}
                        {ntier > 1 && (
                          <span className="flex items-center text-white">
                            <FaGem size={9} />
                            <span className="font-semibold text-[10px] ml-0.5">
                              +{ntier - 1}
                            </span>
                          </span>
                        )}
                      </label>
                      <div className="flex flex-1 gap-1">
                        <button
                          type="button"
                          onClick={() => setNtier(1)}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            ntier === 1
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          1
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNtier(2)
                          }}
                          disabled={
                            !isPlusOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                          }
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            ntier === 2
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${
                            !isPlusOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          2{!isPlusOrHigher && <FaLock size={8} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNtier(3)
                          }}
                          disabled={
                            !isPremiumOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                          }
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            ntier === 3
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${
                            !isPremiumOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          3{!isPremiumOrHigher && <FaLock size={8} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNtier(4)
                          }}
                          disabled={
                            !isStudioOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                          }
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            ntier === 4
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${
                            !isStudioOrHigher ||
                            quality !== 'Ultra' ||
                            charImage ||
                            chillMode
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          4{!isStudioOrHigher && <FaLock size={8} />}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectTheNumberOfImages')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <hr className="my-5 border-[var(--foreground-bg-color)]" />

                {!model.includes('Flame') && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaLightbulb className="text-white" size={12} />
                        {t('creativity')}
                      </span>
                      <div className="flex flex-1 gap-1">
                        <button
                          type="button"
                          onClick={() => setCreativity(70)}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            creativity === 70
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('creative')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCreativity(50)}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            creativity === 50
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('balanced')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCreativity(30)}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            creativity === 30
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('precise')}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('adjustToFollow')}
                    </p>
                  </div>
                )}

                {/* Flame models have no Face Enhance switch: Hardcore Flame's face pass is
                    part of its graph and always runs (backend, 2026-09-24). */}
                {!model.includes('Flame') && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 flex-1">
                        <MdFaceRetouchingNatural
                          className="text-white"
                          size={14}
                        />
                        {t('faceEnhance')}
                        {faceFix && (
                          <span className="flex items-center text-white">
                            <FaGem size={9} />
                            <span className="font-semibold text-[10px] ml-0.5">
                              +1
                            </span>
                          </span>
                        )}
                      </span>
                      <div
                        className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                          faceFix
                            ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                            : 'bg-[var(--foreground-bg-color)]'
                        }`}
                        onClick={toggleFaceFix}
                        role="switch"
                        aria-checked={faceFix}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                            !isStudioOrHigher ||
                            model === 'Anime' ||
                            model === 'Hyperanime' ||
                            model === 'Fur' ||
                            model === 'Furtoon' ||
                            charImage
                              ? 'bg-gray-600'
                              : faceFix
                                ? 'translate-x-6 bg-white'
                                : 'translate-x-0 bg-gray-500'
                          }`}
                        />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('enableForEnhanced')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <BsFillDice3Fill className="text-white" size={12} />
                      {t('seed')}
                    </span>
                    <input
                      type="number"
                      pattern="\d*"
                      id="seed"
                      placeholder={t('seed')}
                      value={seed}
                      onChange={(e) => {
                        setLocked(true)
                        setSeed(e.target.value)
                      }}
                      onWheel={(e) => e.target.blur()}
                      className="appearance-none rounded-xl px-3 h-9 flex-1 min-w-0 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                    />
                    <button
                      type="button"
                      title={t('randomizeSeed')}
                      aria-label={t('randomizeSeed')}
                      onClick={() => randomizeSeed(setSeed)}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-xl text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 shrink-0"
                    >
                      <FiRefreshCw />
                    </button>
                    <button
                      type="button"
                      title={locked ? 'Unlock seed' : 'Lock seed'}
                      aria-label={locked ? 'Unlock seed' : 'Lock seed'}
                      onClick={() => setLocked(!locked)}
                      className={`h-9 w-9 inline-flex items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 shrink-0 ${
                        locked
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      {locked ? <FaLock /> : <FaUnlock />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('enterASeed')}
                  </p>
                </div>

                <hr className="my-5 border-[var(--foreground-bg-color)]" />

                {!banned && !model.includes('Flame') && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaGlobeAmericas className="text-white" size={12} />
                        {t('publish')}
                      </label>
                      <div className="relative flex-1">
                        <select
                          id="privacy"
                          value={
                            visibilityMode === 'Offline' ? 'Offline' : 'Private'
                          }
                          onChange={(e) => {
                            const value = e.target.value
                            if (value === 'Private') {
                              if (!isPlusOrHigher) {
                                setErrorText(
                                  'Sorry, Private mode requires an upgrade. Free users can only use Unsaved mode.',
                                )
                                setIsErrorModalOpen(true)
                                setVisibilityMode('Offline')
                              } else {
                                setVisibilityMode('Private')
                              }
                            } else {
                              setVisibilityMode('Offline')
                            }
                          }}
                          className="appearance-none w-full h-9 rounded-xl px-3 pr-9 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                        >
                          <option value="Private"> {t('private')}</option>
                          <option value="Offline"> {t('unsaved')}</option>
                        </select>
                        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none" />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectPrivate')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 flex-1">
                      <GiSnail className="text-white" size={12} />
                      {t('casualGenerations')}
                    </label>
                    <div
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                        chillMode
                          ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                          : 'bg-[var(--foreground-bg-color)]'
                      }`}
                      onClick={() => {
                        // XL styles take casual since 2026-10-01 (backend myapp/casual.py: only on
                        // GPUs we already pay for); the XL 2 (Flame) models do not. Copy names the
                        // tiers as the picker shows them: 'Ultra' is "Standard", 'Extreme' is "High".
                        if (quality !== 'Ultra' || model.includes('Flame')) {
                          setErrorText(t('casualGenerationsUnavailable'))
                          setIsErrorModalOpen(true)
                        } else if (!chillMode && charImage) {
                          // casual.py limit() refuses AI Character (403); say so here
                          // instead of letting Generate fail.
                          setErrorText(t('casualNoAiCharacter'))
                          setIsErrorModalOpen(true)
                        } else if (isStudioOrHigher) {
                          setChillMode(!chillMode)
                        } else {
                          router.push('/upgrade')
                        }
                      }}
                      role="switch"
                      aria-checked={chillMode}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                          !isStudioOrHigher
                            ? 'bg-gray-600'
                            : chillMode
                              ? 'translate-x-6 bg-white'
                              : 'translate-x-0 bg-gray-500'
                        }`}
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('enableForFree')}
                    {!isStudioOrHigher && (
                      <>
                        {' '}
                        <Link
                          href="/upgrade"
                          className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                        >
                          {t('upgrade')}
                        </Link>
                      </>
                    )}
                  </p>
                </div>

                {visibilityMode === 'Private' && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaFolder className="text-white" size={12} />
                        {t('album')}
                      </label>
                      <div className="relative flex-1">
                        <select
                          id="folder"
                          value={folderSetting}
                          onChange={(e) => setFolderSetting(e.target.value)}
                          className="appearance-none w-full h-9 rounded-xl px-3 pr-9 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                        >
                          {folders.map((folder) => (
                            <option key={folder} value={folder}>
                              {folder}
                            </option>
                          ))}
                        </select>
                        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none" />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('assignAlbum')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 flex-1">
                      <FaSave className="text-white" size={12} />
                      Auto Save
                    </label>
                    <div
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                        autoSave
                          ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                          : 'bg-[var(--foreground-bg-color)]'
                      }`}
                      onClick={() => {
                        if (!user) {
                          setErrorText('Please sign in to use Auto Save.')
                          setIsErrorModalOpen(true)
                          return
                        }
                        if (isSafari) {
                          setErrorText(
                            'Auto Save is not supported on Safari. Please use Chrome/Edge/Firefox.',
                          )
                          setIsErrorModalOpen(true)
                          return
                        }
                        setAutoSave(!autoSave)
                      }}
                      role="switch"
                      aria-checked={autoSave}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                          !user
                            ? 'bg-gray-600'
                            : autoSave
                              ? 'translate-x-6 bg-white'
                              : 'translate-x-0 bg-gray-500'
                        }`}
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    Auto-download generated files (supported browsers only)
                  </p>
                </div>
              </>
            )}
          </>
        </div>
        {!hidePromptAndActions && (
          <div
            style={{
              background:
                'linear-gradient(0deg, rgba(15,13,20,1) 0%, rgba(15,13,20,0.4) 60%, rgba(15,13,20,0) 100%)',
              position: 'sticky',
              bottom: 0,
            }}
            className="z-20 pointer-events-none"
          >
            <div className="px-4 pt-4 pb-10 bg-[var(--middle-bg-color)] border-t border-[var(--foreground-bg-color)] pointer-events-auto">
              <div className="flex flex-wrap justify-center items-center gap-2 mb-3 text-xs">
                <Link href={isPlusOrHigher ? '/gems' : '/upgrade'}>
                  <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer hover:bg-[var(--secondary-bg-color)] transition-colors">
                    <span className="mr-2 font-semibold text-gray-300 text-xs">
                      {t('cost')}
                    </span>
                    {chillMode ? (
                      <span>{t('free')}</span>
                    ) : (
                      <>
                        <FaGem className="mr-1" size={10} />
                        <span className="font-semibold text-xs">
                          {model.includes('Flame')
                            ? ntier * flameImageGems(model) +
                              (quality === 'Max'
                                ? ntier * flameHighDetailGems(model)
                                : 0) +
                              (charImage
                                ? characterVersion === 'v3'
                                  ? 3
                                  : 1
                                : 0)
                            : ntier +
                              (faceFix ? 1 : 0) +
                              (charImage
                                ? characterVersion === 'v3'
                                  ? 3
                                  : 1
                                : 0) +
                              (model.includes('XL') ? ntier : 0) +
                              (quality === 'Extreme'
                                ? 1
                                : quality === 'Max'
                                  ? 2
                                  : 0)}
                        </span>
                      </>
                    )}
                  </div>
                </Link>

                {!chillMode && isPlusOrHigher && (
                  <div
                    className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer hover:bg-[var(--secondary-bg-color)] transition-colors"
                    onClick={() => {
                      setMoreSettingsOpen(true)
                      if (
                        typeof window !== 'undefined' &&
                        window.innerWidth <= 768
                      ) {
                        window.scrollTo({
                          top: document.body.scrollHeight / 2,
                          behavior: 'smooth',
                        })
                      }
                    }}
                  >
                    <BsLightningChargeFill className="mr-2" size={10} />
                    {t('fast')}
                  </div>
                )}

                {chillMode && (
                  <div
                    className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer hover:bg-[var(--secondary-bg-color)] transition-colors"
                    onClick={() => {
                      setMoreSettingsOpen(true)
                      if (
                        typeof window !== 'undefined' &&
                        window.innerWidth <= 768
                      ) {
                        window.scrollTo({
                          top: document.body.scrollHeight / 2,
                          behavior: 'smooth',
                        })
                      }
                    }}
                  >
                    <GiSnail className="mr-2" size={10} />
                    {t('casual')}
                  </div>
                )}

                <div
                  onClick={() => {
                    setMoreSettingsOpen(true)
                    if (
                      typeof window !== 'undefined' &&
                      window.innerWidth <= 768
                    ) {
                      window.scrollTo({
                        top: document.body.scrollHeight / 2,
                        behavior: 'smooth',
                      })
                    }
                  }}
                >
                  {visibilityMode === 'Offline' ? (
                    <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer ring-1 ring-orange-300 hover:bg-[var(--secondary-bg-color)] transition-colors">
                      <IoCloudOffline className="mr-2" size={10} />
                      {t('unsaved')}
                    </div>
                  ) : (
                    <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer ring-1 ring-[var(--main-bg-color)] hover:bg-[var(--secondary-bg-color)] transition-colors">
                      <IoCloud className="mr-2" size={10} />
                      {t('private')}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="submit"
                className={`w-full h-12 px-4 inline-flex justify-center items-center gap-2 rounded-full bg-[var(--main-bg-color)] text-white font-bold transition duration-200 ease-in-out hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isLoading || !prompt ? 'opacity-50' : 'opacity-100'
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ClipLoader
                    color={'#FFF'}
                    loading={isLoading}
                    size={24}
                    aria-label={t('loadingSpinner')}
                    data-testid="loader"
                  />
                ) : cooldown > 0 && chillMode ? (
                  <>
                    <span>{t('coolingDown')}</span>
                    <div className="w-5 h-5">
                      <CircularProgressbar
                        value={cooldownProgress}
                        styles={buildStyles({
                          textColor: '#FFFFFF',
                          pathColor: '#EEEEEE',
                          trailColor: '#7974FF',
                        })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <FaMagic />
                    <span>{t('create')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </>
  )
}

export default Inputs
